"use client";

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { AdCard } from "@/components/ads/Ads";
import { CreatorsRow } from "@/components/ads/Creators";
import type { Page, VideoSummary } from "@/lib/api/types";
import { VideoCard, VideoCardSkeleton } from "./VideoCard";
import { VideoGrid } from "./VideoGrid";
import Link from "@/components/ui/Link";
import { useUi } from "@/components/ui/use-ui";
import { pagePath } from "@/lib/paging";

export type FeedParams = Record<string, string | undefined>;

/** With `ads`, a card banner takes the 5th place and then every 10th (AdCard shows nothing without one). */
const isAdSpot = (i: number) => i >= 4 && (i - 4) % 10 === 0;
/** With `creators`, the OnlyFans creators' row (full width) comes before this video. */
const CREATORS_AT = 12;

/**
 * Server renders page 1 (or the page asked for); further pages stream in from /api/feed as the
 * visitor scrolls (beeg has no pagination, only endless scroll). With `pagePath`, a link to the next
 * page's own address follows the videos, for search engines (lib/paging.ts).
 * Give it a `key` derived from the params so a new filter starts fresh.
 */
export function InfiniteFeed({
  initial,
  params,
  empty,
  hideFirstOnWide = 0,
  ads = false,
  creators = false,
  pagePath: listPath,
}: {
  initial: Page<VideoSummary>;
  params: FeedParams;
  empty?: React.ReactNode;
  /** The first cards are already shown beside the player on wide screens (WatchLayout): hide them there. */
  hideFirstOnWide?: number;
  /** Card banners of the page's ad network among the videos. */
  ads?: boolean;
  /** A row of OnlyFans creators (CreatorsRow) among the videos. */
  creators?: boolean;
  /** The list's own address (its other query kept), for the link to its next page. */
  pagePath?: string;
}) {
  const t = useUi();
  const [items, setItems] = useState(initial.items);
  const [hasMore, setHasMore] = useState(initial.hasMore);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const pageRef = useRef(initial.page);
  const [page, setPage] = useState(initial.page);
  const busy = useRef(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const query = useRef(params);

  const loadMore = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    setError(false);
    try {
      const search = new URLSearchParams();
      for (const [k, v] of Object.entries(query.current)) if (v) search.set(k, v);
      search.set("page", String(pageRef.current + 1));
      const res = await fetch(`/api/feed?${search}`);
      if (!res.ok) throw new Error(String(res.status));
      const next: Page<VideoSummary> = await res.json();
      pageRef.current = next.page;
      setPage(next.page);
      setItems((prev) => {
        const seen = new Set(prev.map((v) => v.id));
        return [...prev, ...next.items.filter((v) => !seen.has(v.id))];
      });
      setHasMore(next.hasMore);
    } catch {
      setError(true);
    } finally {
      busy.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || error) return;
    const io = new IntersectionObserver((entries) => entries[0]?.isIntersecting && loadMore(), { rootMargin: "1400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, error, loadMore, items.length]);

  if (items.length === 0 && !hasMore && !loading) return <>{empty}</>;

  return (
    <>
      <VideoGrid>
        {items.map((v, i) => (
          <Fragment key={v.id}>
            {creators && i === CREATORS_AT && <CreatorsRow className="col-span-full" />}
            {ads && isAdSpot(i) && <AdCard className={i < hideFirstOnWide ? "xl:hidden" : undefined} />}
            {i < hideFirstOnWide ? (
              <div className="min-w-0 xl:hidden">
                <VideoCard video={v} eager={i < 5} priority={i < 4} />
              </div>
            ) : (
              <VideoCard video={v} eager={i < 5} priority={i < 4} />
            )}
          </Fragment>
        ))}
        {(loading || (items.length === 0 && hasMore)) &&
          Array.from({ length: items.length ? 5 : 15 }, (_, i) => <VideoCardSkeleton key={`s${i}`} />)}
      </VideoGrid>
      <div ref={sentinel} className="h-px" />
      {error && (
        <div className="mt-8 flex justify-center">
          <button type="button" onClick={loadMore} className="chip h-10 rounded-full px-5 text-[14px] text-soft">
            {t.video.loadMoreFailed}
          </button>
        </div>
      )}
      {listPath && hasMore && (
        <div className="mt-8 flex justify-center">
          <Link href={pagePath(listPath, page + 1)} rel="next" className="chip h-10 rounded-full px-5 text-[14px] leading-10 text-soft hover:text-fg">
            {t.video.nextPage} →
          </Link>
        </div>
      )}
      {!hasMore && items.length > 10 && <p className="mt-12 text-center text-[13px] text-faint">{t.video.end}</p>}
    </>
  );
}
